import type { CSSProperties } from 'react';
import { can } from '@domain';
import {
  buildPayload,
  computeTotals,
  formatMoney,
  lineAmount,
  summariseChecks,
  validateDocument,
  type DocumentKind,
  type LineAmountType,
  type ReferenceData,
} from '@domain';
import { Banner, Card, buttonCard, buttonQuiet, pillStyle } from '../components/ui.js';
import { accountTypeLabel, groupAccounts } from '../data/accounts.js';
import { FORMS, type FieldDef, type FieldType } from '../data/forms.js';
import type { Translate } from '../i18n/index.js';
import type { RenderMessage } from '../i18n/messages.js';
import type { TrainingDelivery } from '../api.js';
import { pickForLanguage } from '@domain';
import { SearchableSelect, type SelectOption } from '../components/SearchableSelect.js';
import { theme } from '../theme.js';
import { parseCsv, type Action, type AppState, type Role } from '../state.js';

interface Option {
  value: string;
  label: string;
}

interface FormScreenProps {
  kind: DocumentKind;
  state: AppState;
  role: Role;
  rtl: boolean;
  orgName: string;
  /** This company's own tax basis, so a cleared form goes back to it. */
  defaultAmountsAre?: LineAmountType;
  reference: ReferenceData;
  knownReferences: string[];
  /** Open training courses a bill can be put on. Empty for most companies. */
  deliveries: TrainingDelivery[];
  t: Translate;
  renderMessage: RenderMessage;
  dispatch: (action: Action) => void;
  onPush: () => void;
}

const inputStyle: CSSProperties = {
  width: '100%',
  height: '34px',
  border: `1px solid ${theme.borderStrong}`,
  borderRadius: '12px',
  padding: '0 9px',
  fontSize: '13px',
};

const lineControl: CSSProperties = {
  width: '100%',
  height: '32px',
  border: `1px solid ${theme.borderStrong}`,
  borderRadius: theme.radiusPill,
  padding: '0 8px',
  fontSize: '12.5px',
};

/**
 * Not a real item code, and deliberately unlike one: it is a command sitting in
 * a list of choices, the way Xero's own account dropdown offers "+ Add new
 * account…".
 */
const NEW_ITEM = '__add_new_item__';

// Xero's account types in words, and the grouping the dropdowns use. Shared
// with the money screens; see data/accounts.ts.

export function FormScreen({
  kind,
  state,
  role,
  rtl,
  orgName,
  defaultAmountsAre,
  reference,
  knownReferences,
  deliveries,
  t,
  renderMessage,
  dispatch,
  onPush,
}: FormScreenProps) {
  const form = FORMS[kind];
  const doc = state.docs[kind];
  const fields = doc.fields;
  const lines = doc.lines;

  /** Stated with no tax, so the lines carry no rate and Xero is sent none. */
  const noTax = fields.amountsAre === 'NoTax';
  /**
   * Anyone who may prepare a document may also file it. True of every role
   * today — it is written as the policy rather than as a constant so that a
   * read-only role, if one is ever added, is refused here without anybody
   * having to remember this screen.
   */
  const mayPrepare = can(role, 'submit');

  /*
   * What is left of the old distinction: the payload view, which is a
   * diagnostic rather than something the work needs. Filing itself is no
   * longer gated — an organisation's own staff file into their own books
   * without anybody countersigning.
   */
  const isAdmin = can(role, 'operate');
  const amountsAre = (typeof fields.amountsAre === 'string' ? fields.amountsAre : 'Exclusive') as LineAmountType;

  /**
   * What an empty list means, said plainly.
   *
   * These lists come from Xero. An organisation that has just been connected
   * genuinely has no contacts and no products yet, and a control that is simply
   * blank looks broken rather than empty — clicking it opens a dropdown with
   * nothing in it, which is a black rectangle and no explanation.
   */
  const emptyLabelFor = (type: FieldType): string => {
    switch (type) {
      case 'contact':
        return t('No contacts in Xero for this organisation yet');
      case 'account':
        return t('No accounts found in Xero');
      case 'tax':
        return t('No tax rates found in Xero');
      case 'currency':
        return t('No currencies found in Xero');
      case 'training':
        return t('No courses are open');
      default:
        return t('Nothing to choose from');
    }
  };

  /**
   * What an empty picker says, when the field is a restricted account one.
   *
   * "No accounts found in Xero" is wrong here and sends somebody looking
   * for a connection problem. The chart of accounts is fine; it just has
   * nothing of the one type this field takes, and the fix is to create
   * that account in Xero. Saying which type is the whole message.
   */
  const emptyAccountLabel = (field: FieldDef): string => {
    if (field.type !== 'account' || !field.accounts || reference.accounts.length === 0) {
      return emptyLabelFor(field.type);
    }
    const names = field.accounts.map((type) => t(accountTypeLabel(type))).join(', ');
    return `${t('No account of this kind in Xero yet')} — ${names}`;
  };

  /** Lists that come from Xero, as opposed to fixed choices with a sensible default. */
  const isFromXero = (type: FieldType): boolean =>
    type === 'contact' || type === 'account' || type === 'tax' || type === 'currency';

  /**
   * Accounts grouped by what they are, the way Xero's own screens group them.
   *
   * An accountant picking an account looks for it under a heading — Expenses,
   * Revenue, Current Liability. A flat run of several hundred codes is
   * technically the same information and practically unusable.
   *
   * Xero's type names are left exactly as Xero writes them. These are not our
   * words to translate: a company keeping its books in Arabic already has
   * Arabic account names, and putting Xero's own data through our phrase
   * dictionary would rewrite whichever entries happened to collide with it.
   */
  const accountGroups = (
    /*
      The account types this field accepts, where Xero restricts them.

      Undefined means all of them, which is most fields. The restricted
      ones are the item form's inventory asset and cost of goods sold
      accounts: Xero takes only INVENTORY and only DIRECTCOSTS there, and
      this picker used to offer the whole chart — so the first thing it
      showed under "Inventory asset account" was the bank accounts.
    */
    allowed?: readonly string[],
  ): { label: string; accounts: typeof reference.accounts }[] =>
    groupAccounts(reference.accounts)
      .filter((group) => allowed === undefined || allowed.includes(group.type))
      .map((group) => ({ label: t(accountTypeLabel(group.type)), accounts: group.accounts }))
      .sort((a, b) => a.label.localeCompare(b.label));

  const optionsFor = (type: FieldType): Option[] => {
    switch (type) {
      case 'contact':
        return reference.contacts.map((c) => ({ value: c.id, label: c.name }));
      case 'currency':
        return reference.currencies.map((c) => ({ value: c, label: c }));
      case 'account':
        return reference.accounts.map((a) => ({
          value: a.code,
          label: `${a.code} · ${pickForLanguage(a.name, a.description, state.lang)}`,
        }));
      case 'tax':
        return reference.taxRates.map((r) => ({ value: r.type, label: t(r.name) }));
      case 'amounts':
        return (['Exclusive', 'Inclusive', 'NoTax'] as const).map((v) => ({
          value: v,
          label: v === 'NoTax' ? t('No tax') : v === 'Exclusive' ? t('Tax exclusive') : t('Tax inclusive'),
        }));
      case 'training':
        /*
          Closed courses are left out. Their books are shut, the server
          would refuse the link, and offering one here would be an offer
          that cannot be taken.
        */
        return deliveries
          .filter((delivery) => delivery.closedAt === null)
          .map((delivery) => ({
            value: String(delivery.deliveryId),
            label: `${delivery.name}  ·  ${delivery.startsOn}`,
          }));
      default:
        return [];
    }
  };

  const checks = validateDocument(kind, fields, lines, {
    contacts: reference.contacts,
    taxRates: reference.taxRates,
    knownReferences,
  });
  const summary = summariseChecks(checks);
  const locked = isAdmin && !summary.canPush;

  const totals = computeTotals(lines, amountsAre, reference.taxRates);
  const payload = buildPayload(kind, fields, lines, amountsAre, reference.contacts);

  const showDiscount = form.discount;
  const lineGrid = `130px minmax(220px,1fr) 68px 96px ${showDiscount ? '68px ' : ''}168px 168px 104px 30px`;
  const lineColumns = ['Item', 'Description', 'Qty', 'Unit Price', ...(showDiscount ? ['Disc.'] : []), 'Account', 'Tax Rate', 'Amount', ''];
  const numericHeaders = ['Qty', 'Unit Price', 'Disc.', 'Amount'];

  const csvLines = parseCsv(state.csvText, form.discount);

  const renderField = (field: FieldDef) => {
    const value = fields[field.k];
    const isSelect = ['contact', 'currency', 'account', 'tax', 'amounts', 'training'].includes(
      field.type,
    );

    /*
      A company that runs no courses never sees this box. Showing an empty
      dropdown labelled "For a training course" on every bill would be a
      question most clients can only answer with silence.
    */
    if (field.type === 'training' && deliveries.length === 0) return null;

    /*
      Some fields only exist in Xero under a condition — the inventory
      asset and cost of goods sold accounts exist only for a tracked item,
      and Xero rejects them on an untracked one. They used to be drawn
      always, which asked for an answer that was then thrown away on the
      way out. See buildPayload in api/src/domain/payloads.ts.
    */
    if (field.onlyWhen && fields[field.onlyWhen] !== true) return null;

    return (
      <div
        key={field.k}
        style={{
          flex: `1 1 calc(${((field.span / 12) * 100).toFixed(2)}% - 16px)`,
          minWidth: field.span >= 6 ? '260px' : '170px',
        }}
      >
        <label style={{ display: 'block', marginBottom: '5px' }}>
          <span style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: theme.label }}>{t(field.label)}</span>
            {field.req && <span style={{ color: '#A94442', fontSize: '12px' }}>*</span>}
          </span>
          <span
            style={{
              display: 'block',
              fontFamily: theme.fontMono,
              fontSize: '10.5px',
              color: theme.textFaint,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {field.api}
          </span>
        </label>

        {isSelect &&
          (() => {
            /*
              An account field that Xero restricts counts its own options,
              not the whole chart. Otherwise a company with no inventory
              account gets a picker that reports itself full and opens
              empty.
            */
            const groups = field.type === 'account' ? accountGroups(field.accounts) : [];
            const accountOptions = groups.flatMap((group) =>
              group.accounts.map((account) => ({
                value: account.code,
                label: `${account.code} · ${pickForLanguage(account.name, account.description, state.lang)}`,
                group: group.label,
              })),
            );

            const options = field.type === 'account' ? accountOptions : optionsFor(field.type);
            const fromXero = isFromXero(field.type);
            const empty = options.length === 0;

            // A short fixed list is quicker to use as a plain dropdown; a list
            // that comes from Xero can be hundreds long and has to be typed into.
            if (!fromXero) {
              return (
                <select
                  value={String(value ?? '')}
                  onChange={(e) =>
                    dispatch({ type: 'setField', kind, key: field.k, value: e.target.value })
                  }
                  style={{
                    ...inputStyle,
                    borderRadius: theme.radiusControl,
                    background: '#fff',
                    padding: '0 8px',
                  }}
                >
                  {/*
                    A course is optional, and saying so has to be an
                    option somebody can see and choose.

                    Without this the browser has nothing matching the
                    empty value, so it displays the first course while the
                    field is still blank — the bill goes to Xero attached
                    to nothing while the screen says otherwise. Most bills
                    a company files are not for a course at all, so this
                    is also the common answer and belongs first.
                  */}
                  {field.type === 'training' && (
                    <option value="">{t('Not for a course')}</option>
                  )}
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              );
            }

            return (
              <SearchableSelect
                value={String(value ?? '')}
                options={options as SelectOption[]}
                placeholder={empty ? emptyAccountLabel(field) : t('Type to search…')}
                emptyLabel={emptyAccountLabel(field)}
                disabled={empty}
                invalid={field.req === true && !value}
                createLabel={field.type === 'contact' && kind !== 'contact' ? t('+ Add a contact') : undefined}
                onCreate={
                  field.type === 'contact' && kind !== 'contact'
                    ? () =>
                        dispatch({
                          type: 'goAndReturn',
                          screen: 'form:contact',
                          back: { screen: `form:${kind}`, kind, field: field.k },
                        })
                    : undefined
                }
                onChange={(next) => dispatch({ type: 'setField', kind, key: field.k, value: next })}
                t={t}
              />
            );
          })()}

        {/*
          Kept alongside the list as well as inside it: somebody who already
          knows the contact is missing should not have to open a list to find
          the way to add one.
        */}
        {isSelect && field.type === 'contact' && kind !== 'contact' && (
          <button
            type="button"
            onClick={() =>
              dispatch({
                type: 'goAndReturn',
                screen: 'form:contact',
                back: { screen: `form:${kind}`, kind, field: field.k },
              })
            }
            style={{
              marginTop: '5px',
              background: 'none',
              border: 0,
              padding: 0,
              color: theme.ink,
              fontSize: '11.5px',
              fontWeight: 600,
              cursor: 'pointer',
              textAlign: 'start',
            }}
          >
            {t('+ Add a contact')}
          </button>
        )}

        {field.type === 'toggle' && (
          <button
            onClick={() => dispatch({ type: 'setField', kind, key: field.k, value: !value })}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              height: '34px',
              width: '100%',
              padding: '0 10px',
              border: `1px solid ${theme.borderStrong}`,
              borderRadius: theme.radiusControl,
              background: value ? theme.goodBg : '#fff',
              color: value ? theme.goodDeep : theme.textMuted,
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                width: '9px',
                height: '9px',
                borderRadius: '50%',
                background: value ? theme.good : '#E2E2E5',
                flex: '0 0 auto',
              }}
            />
            <span style={{ fontSize: '12.5px', fontWeight: 600 }}>{value ? t('On — stock valued') : t('Off')}</span>
          </button>
        )}

        {field.type === 'area' && (
          <textarea
            value={String(value ?? '')}
            onChange={(e) => dispatch({ type: 'setField', kind, key: field.k, value: e.target.value })}
            placeholder={field.ph ? t(field.ph) : ''}
            rows={3}
            style={{
              width: '100%',
              border: `1px solid ${theme.borderStrong}`,
              borderRadius: theme.radiusControl,
              padding: '8px',
              fontSize: '13px',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        )}

        {!isSelect && field.type !== 'toggle' && field.type !== 'area' && (
          <input
            value={String(value ?? '')}
            onChange={(e) => dispatch({ type: 'setField', kind, key: field.k, value: e.target.value })}
            type={field.type === 'date' ? 'date' : 'text'}
            placeholder={field.ph ? t(field.ph) : ''}
            style={{
              ...inputStyle,
              fontFamily: field.type === 'date' || field.type === 'num' ? theme.fontMono : 'inherit',
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', padding: '22px 24px 48px', alignItems: 'flex-start', maxWidth: '1560px' }}>
      <div style={{ flex: '1 1 520px', minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <button
            onClick={() => dispatch({ type: 'go', screen: form.backTo })}
            aria-label="Back"
            style={{ ...buttonCard, width: '30px', height: '30px', padding: 0, display: 'grid', placeItems: 'center' }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#515C6B" strokeWidth="2" style={{ transform: rtl ? 'rotate(180deg)' : 'none' }}>
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <h1 style={{ margin: 0, fontSize: '21px', fontWeight: 600, letterSpacing: '-.01em', whiteSpace: 'nowrap' }}>
            {t(form.title)}
          </h1>
          <span style={{ ...pillStyle('DRAFT'), letterSpacing: '.08em', fontSize: '11px' }}>{t('DRAFT')}</span>
          <span style={{ fontFamily: theme.fontMono, fontSize: '11.5px', color: '#5A6676' }}>{form.api}</span>
          <div style={{ flex: '1 1 12px' }} />
          <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
            <button
              onClick={() => dispatch({ type: 'resetForm', kind, amountsAre: defaultAmountsAre })}
              style={{ background: 'transparent', border: 0, color: theme.textMuted, fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', padding: '9px 10px' }}
            >
              {t('Discard')}
            </button>
            {isAdmin && (
              <button onClick={() => dispatch({ type: 'togglePayload' })} style={buttonCard}>
                {state.showPayload ? t('Hide payload') : t('Show payload')}
              </button>
            )}
            <button
              onClick={onPush}
              disabled={locked}
              style={{
                background: locked ? theme.border : theme.nav,
                color: locked ? theme.textSubtle : '#fff',
                border: 0,
                borderRadius: theme.radiusControl,
                padding: '9px 15px',
                fontWeight: 600,
                fontSize: '12.5px',
                whiteSpace: 'nowrap',
                cursor: locked ? 'default' : 'pointer',
              }}
            >
              {/*
                One label, because there is one outcome. The form used to
                offer DRAFT or AUTHORISED and the button changed to match;
                a document that waits in Xero for a second approval is work
                done twice, and the practice removed the choice.
              */}
              {locked ? t('Fix issues to push') : t('File in Xero as approved')}
            </button>
          </div>
        </div>

        {mayPrepare && (
          <Card style={{ marginBottom: '14px' }}>
            <div
              style={{
                padding: '12px 16px',
                borderBottom: `1px solid ${theme.dividerSoft}`,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '10px',
                background: theme.surfaceHeader,
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '13px' }}>{t('Pre-push checks')}</div>
              <span style={pillStyle(summary.blocking ? 'BLOCKED' : summary.warnings ? 'REVIEW' : 'READY')}>
                {summary.blocking
                  ? `${summary.blocking} ${t('blocking issues')}`
                  : summary.warnings
                    ? t('Ready, with warnings')
                    : t('Ready to push')}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '10px 20px', padding: '14px 16px' }}>
              {checks.map((check) => (
                <div key={check.label} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <span
                    style={{
                      width: '18px',
                      height: '18px',
                      borderRadius: theme.radiusPill,
                      flex: '0 0 auto',
                      display: 'grid',
                      placeItems: 'center',
                      fontSize: '11px',
                      fontWeight: 700,
                      background: check.ok ? theme.goodBg : check.warn ? '#F8F3E7' : '#F8EDEC',
                      color: check.ok ? theme.goodDeep : check.warn ? '#7E590F' : theme.badDeep,
                    }}
                  >
                    {check.ok ? '✓' : check.warn ? '!' : '✕'}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '12.5px', fontWeight: 600, color: theme.text }}>
                      {t(check.label)}
                    </div>
                    <div style={{ fontSize: '11.5px', color: theme.textMuted, marginTop: '2px' }}>
                      {renderMessage(check.detail)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {state.pushError && state.pushError.length > 0 && (
          <div
            style={{
              background: theme.badBg,
              border: `1px solid ${theme.bad}33`,
              borderRadius: theme.radiusCard,
              padding: '13px 16px',
              marginBottom: '14px',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: '13px', color: theme.badDeep }}>
              {t('Xero did not accept this document')}
            </div>
            <ul
              style={{
                margin: '7px 0 0',
                paddingInlineStart: '18px',
                fontSize: '12.5px',
                lineHeight: 1.7,
                color: theme.badDeep,
              }}
            >
              {state.pushError.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
            <div style={{ marginTop: '7px', fontSize: '11.5px', color: theme.textMuted }}>
              {t('Nothing was saved. Correct the document and try again.')}
            </div>
          </div>
        )}

        {state.saved && (
          <Banner>
            <span style={{ fontWeight: 600 }}>
              {`${t('Filed in Xero')} — ${orgName}`}
            </span>
          </Banner>
        )}

        {form.sections.map((section) => (
          <Card key={section.title} style={{ marginBottom: '14px' }}>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.dividerSoft}`, fontWeight: 600, fontSize: '13px', background: theme.surfaceHeader }}>
              {t(section.title)}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 16px', padding: '16px' }}>
              {section.fields.map(renderField)}
            </div>
          </Card>
        ))}

        {form.lines && (
          <Card>
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.dividerSoft}`, display: 'flex', alignItems: 'center', gap: '10px', background: theme.surfaceHeader }}>
              <div style={{ fontWeight: 600, fontSize: '13px' }}>{t('Line items')}</div>
              <span style={{ fontFamily: theme.fontMono, fontSize: '10.5px', color: theme.textFaint }}>LineItems[]</span>
            </div>

            <div style={{ overflowX: 'auto' }}>
              <div style={{ width: 'max-content', minWidth: '100%' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: lineGrid,
                    gap: '8px',
                    padding: '8px 16px',
                    background: theme.surfaceMuted,
                    borderBottom: `1px solid ${theme.dividerSoft}`,
                    fontSize: '10.5px',
                    fontWeight: 700,
                    letterSpacing: '.07em',
                    textTransform: 'uppercase',
                    color: '#5A6676',
                  }}
                >
                  {lineColumns.map((label, i) => (
                    <div key={`${label}-${i}`} style={{ textAlign: numericHeaders.includes(label) ? (rtl ? 'left' : 'right') : 'start' }}>
                      {label ? t(label) : ''}
                    </div>
                  ))}
                </div>

                {lines.map((line, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: lineGrid,
                      gap: '8px',
                      alignItems: 'center',
                      padding: '7px 16px',
                      borderBottom: '1px solid #F7F3EF',
                    }}
                  >
                    <select
                      value={line.itemCode}
                      onChange={(e) => {
                        const code = e.target.value;

                        // Chosen from inside the list, so the line it belongs to
                        // is known — which is what lets it come back selected
                        // rather than merely created.
                        if (code === NEW_ITEM) {
                          dispatch({
                            type: 'goAndReturn',
                            screen: 'form:item',
                            back: { screen: `form:${kind}`, kind, field: `line:${index}` },
                          });
                          return;
                        }

                        dispatch({ type: 'setLine', kind, index, key: 'itemCode', value: code });
                        const item = reference.items.find((i) => i.code === code);
                        if (item) {
                          /*
                            Which side of the item applies. Taken from the
                            document rather than from whether it happens to
                            show a discount column, so price and account can
                            never disagree about what is being prepared.
                          */
                          const sales = kind === 'invoice' || kind === 'quote';

                          dispatch({ type: 'setLine', kind, index, key: 'description', value: item.name });
                          dispatch({
                            type: 'setLine',
                            kind,
                            index,
                            key: 'price',
                            value: String(sales ? item.sell : item.buy),
                          });

                          /*
                            Xero told us where this item posts, and a line with
                            no account code cannot be filed at all.

                            Set even when the item does not say, which clears
                            the box. Keeping the previous item's account looks
                            helpful and is the more dangerous of the two: it is
                            a wrong answer wearing the shape of a right one,
                            and it would post to an account nobody chose for
                            this line. An empty box asks the question instead,
                            and the validator refuses to file until it is
                            answered.
                          */
                          dispatch({
                            type: 'setLine',
                            kind,
                            index,
                            key: 'accountCode',
                            value: sales ? item.sellAccount : item.buyAccount,
                          });
                        }
                      }}
                      style={{ ...lineControl, background: '#fff', padding: '0 6px' }}
                    >
                      {/*
                        Items come from Xero, so their names are not ours to translate.
                      */}
                      <option value="">—</option>
                      {kind !== 'item' && <option value={NEW_ITEM}>{t('+ Add a product or service')}</option>}
                      {reference.items.map((item) => (
                        <option key={item.code} value={item.code}>
                          {(() => {
                            const label = pickForLanguage(item.name, item.description, state.lang);
                            return label ? `${item.code} · ${label}` : item.code;
                          })()}
                        </option>
                      ))}
                    </select>

                    <input
                      value={line.description}
                      onChange={(e) => dispatch({ type: 'setLine', kind, index, key: 'description', value: e.target.value })}
                      placeholder={t('Description')}
                      style={lineControl}
                    />
                    <input
                      value={line.qty}
                      onChange={(e) => dispatch({ type: 'setLine', kind, index, key: 'qty', value: e.target.value })}
                      style={{ ...lineControl, fontFamily: theme.fontMono, textAlign: 'end' }}
                    />
                    <input
                      value={line.price}
                      onChange={(e) => dispatch({ type: 'setLine', kind, index, key: 'price', value: e.target.value })}
                      style={{ ...lineControl, fontFamily: theme.fontMono, textAlign: 'end' }}
                    />
                    {showDiscount && (
                      <input
                        value={line.disc}
                        onChange={(e) => dispatch({ type: 'setLine', kind, index, key: 'disc', value: e.target.value })}
                        style={{ ...lineControl, fontFamily: theme.fontMono, textAlign: 'end' }}
                      />
                    )}
                    {/*
                      The longest list in the product. A chart of accounts runs
                      to hundreds of lines, and picking one by scrolling is not
                      something to ask of somebody entering a stack of invoices.
                      Typing a code or part of a name narrows it; Arabic-Indic
                      digits find Western codes.
                    */}
                    <SearchableSelect
                      value={line.accountCode}
                      options={accountGroups().flatMap((group) =>
                        group.accounts.map((account) => ({
                          value: account.code,
                          label: `${account.code} · ${pickForLanguage(account.name, account.description, state.lang)}`,
                          group: group.label,
                        })),
                      )}
                      placeholder={t('Account')}
                      invalid={!line.accountCode}
                      onChange={(next) => dispatch({ type: 'setLine', kind, index, key: 'accountCode', value: next })}
                      t={t}
                      style={{ ...lineControl, padding: '0 6px' }}
                    />
                    {/*
                      No tax means no rate, and Xero enforces it: "The
                      TaxType code INPUT cannot be used when Line Amount
                      Types is set to NoTax" — it refuses the document
                      outright.

                      So on a NoTax document the cell says so rather than
                      offering a choice. The rate is not cleared from the
                      form, only left unsent, so switching the header back
                      to Exclusive finds it still there.
                    */}
                    {noTax ? (
                      <div
                        style={{
                          ...lineControl,
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 8px',
                          background: theme.surfaceSunken,
                          color: theme.textSubtle,
                          fontSize: '12px',
                        }}
                        title={t('This document is stated with no tax, so lines carry no rate.')}
                      >
                        {t('No tax')}
                      </div>
                    ) : (
                      <SearchableSelect
                        value={line.taxType}
                        options={reference.taxRates.map((rate) => ({ value: rate.type, label: rate.name }))}
                        placeholder={t('Tax rate')}
                        onChange={(next) => dispatch({ type: 'setLine', kind, index, key: 'taxType', value: next })}
                        t={t}
                        style={{ ...lineControl, padding: '0 6px' }}
                      />
                    )}
                    <div style={{ fontFamily: theme.fontMono, fontSize: '12.5px', textAlign: 'end', paddingInlineEnd: '2px' }}>
                      {formatMoney(lineAmount(line))}
                    </div>
                    <button
                      onClick={() => dispatch({ type: 'removeLine', kind, index })}
                      aria-label="Remove line"
                      style={{
                        width: '26px',
                        height: '26px',
                        border: 0,
                        background: 'transparent',
                        borderRadius: theme.radiusPill,
                        color: theme.textFaint,
                        cursor: 'pointer',
                        display: 'grid',
                        placeItems: 'center',
                      }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', padding: '14px 16px', borderTop: `1px solid ${theme.dividerSoft}` }}>
              <div style={{ flex: 1, minWidth: '220px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <button onClick={() => dispatch({ type: 'addLine', kind })} style={buttonQuiet}>
                    {t('Add another line')}
                  </button>
                  {mayPrepare && (
                    <button onClick={() => dispatch({ type: 'toggleCsv' })} style={buttonQuiet}>
                      {state.csvOpen ? t('Close import') : t('Import CSV / Excel')}
                    </button>
                  )}
                </div>
                <span style={{ fontSize: '11.5px', color: theme.textSubtle }}>
                  {t('Description, Account and Tax Rate are required per line.')}
                </span>

                {state.csvOpen && (
                  <div style={{ width: '100%', background: theme.surfaceMuted, border: `1px solid ${theme.border}`, borderRadius: theme.radiusControl, padding: '12px' }}>
                    <div style={{ fontSize: '11.5px', color: theme.textMuted, marginBottom: '8px' }}>
                      {t('One line per row: description, qty, unit price [, account code, tax type]. Paste from Excel — tabs or commas both work.')}
                    </div>
                    <textarea
                      value={state.csvText}
                      onChange={(e) => dispatch({ type: 'setCsvText', text: e.target.value })}
                      rows={4}
                      placeholder="Chicken breast (kg), 120, 18.2, 310, INPUT"
                      style={{
                        width: '100%',
                        border: `1px solid ${theme.borderStrong}`,
                        borderRadius: '12px',
                        padding: '8px',
                        fontFamily: theme.fontMono,
                        fontSize: '12px',
                        resize: 'vertical',
                      }}
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '8px' }}>
                      <button
                        onClick={() => csvLines.length && dispatch({ type: 'appendLines', kind, lines: csvLines })}
                        style={{
                          background: theme.nav,
                          color: '#fff',
                          border: 0,
                          borderRadius: theme.radiusControl,
                          padding: '8px 13px',
                          fontSize: '12.5px',
                          fontWeight: 600,
                          cursor: csvLines.length ? 'pointer' : 'default',
                        }}
                      >
                        {csvLines.length ? `${t('Add')} ${csvLines.length} ${t('lines')}` : t('Nothing to add yet')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ width: '270px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '12.5px', color: '#515C6B' }}>
                  <span>{t('Subtotal')}</span>
                  <span style={{ fontFamily: theme.fontMono }}>{formatMoney(totals.sub)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', fontSize: '12.5px', color: '#515C6B' }}>
                  <span>{t('Total tax')}</span>
                  <span style={{ fontFamily: theme.fontMono }}>{formatMoney(totals.tax)}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '12px',
                    fontSize: '15px',
                    fontWeight: 700,
                    color: theme.text,
                    borderTop: `1px solid ${theme.dividerSoft}`,
                    paddingTop: '9px',
                  }}
                >
                  <span>{t('Total')} QAR</span>
                  <span style={{ fontFamily: theme.fontMono }}>{formatMoney(totals.total)}</span>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>

      {isAdmin && state.showPayload && (
        <aside
          style={{
            flex: '1 1 320px',
            minWidth: '280px',
            maxWidth: '340px',
            position: 'sticky',
            top: 0,
            background: theme.nav,
            borderRadius: theme.radiusControl,
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '12px 14px', borderBottom: `1px solid ${theme.navBorder}`, display: 'flex', alignItems: 'center', gap: '9px' }}>
            <div style={{ color: theme.navTextBright, fontWeight: 600, fontSize: '12.5px' }}>{t('Xero payload preview')}</div>
            <div style={{ flex: 1 }} />
            <div style={{ fontFamily: theme.fontMono, fontSize: '10.5px', color: '#B09E80' }}>{form.endpoint}</div>
          </div>
          <pre
            dir="ltr"
            style={{
              margin: 0,
              padding: '14px',
              color: '#E8E8EB',
              fontFamily: theme.fontMono,
              fontSize: '11px',
              lineHeight: 1.65,
              maxHeight: '520px',
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {JSON.stringify(payload, null, 2)}
          </pre>
          <div style={{ padding: '12px 14px', borderTop: `1px solid ${theme.navBorder}`, color: theme.navTextDim, fontSize: '11.5px', lineHeight: 1.6 }}>
            {t('Sent by the backend, which owns the OAuth tokens. Created as AUTHORISED with an Idempotency-Key so a retry cannot duplicate.')}
          </div>
        </aside>
      )}
    </div>
  );
}
